import { useIncidentDetail } from 'core/components/JianDetail/hooks/useIncidentDetail';
import { formatDateTime } from 'core/utils/date';
import useEnumTranslator from './useEnumTranslator';

export type IncidentDetailForSendai = ReturnType<
  typeof useIncidentDetailForSendai
>['incidentInfo'];
export type IncidentDetailEmsTeamForSendai = ReturnType<
  typeof useIncidentDetailForSendai
>['emsTeamInfo'];
export type IncidentDetailPatientsForSendai = ReturnType<
  typeof useIncidentDetailForSendai
>['patients'];
export type IncidentDetailPatientForSendai = ReturnType<
  typeof useIncidentDetailForSendai
>['patients'][number];
export type PatientTimeSeriesForSendai = ReturnType<
  typeof useIncidentDetailForSendai
>['patients'][number]['timeSeries'];
export type PatientCpaModeForSendai = ReturnType<
  typeof useIncidentDetailForSendai
>['patients'][number]['cpaMode'];

export const useIncidentDetailForSendai = () => {
  const { incidentDetailData } = useIncidentDetail();
  const {
    getIncidentTypeLabel,
    getIncidentSectionLabel,
    getIncidentActivityTypeLabel,
    getIncidentDispatchStatusLabel,
    getIncidentRoadTypeLabel,
    getIncidentRoadDirectionLabel,
    getIncidentTelegramTargetAddressTypeLabel,
    getPatientSexLabel,
    getOccupationLabel,
    getEmsTeamMemberPositionLabel,
    getAmbulanceWithTrainingLabel,
    getWelfareDistrictLabel,
    getCpaInitialEcgLabel,
    getMedicalHistoryLabel,
    getADLLabel,
    getADLExcretionLabel,
    getADLCommunicationLabel,
    getTransportBodyPositionLabel,
    getECGWaveformLabel,
    getECGRemarksLabel,
    getMeasureTimingLabel,
    getPupilReactionLabel,
    getO2MeansLabel,
    getFacialAppearanceLabel,
    getFacialExpressionLabel,
    getPositionLabel,
    getGeneralTreatmentsLabel,
    getAccompanyingLabel,
    getBystanderTypeLabel,
    getBystanderTreatmentLabel,
    getBystanderTreatmentPersonLabel,
    getAedCountLabel,
    getCollaborationLabel,
    getCollaborationOther1Label,
    getCollaborationOther2Label,
    getCollaborationTimingLabel,
    getDrHeliRequestedTimingLabel,
  } = useEnumTranslator();

  const { incidentInfo, emsTeamInfo, patientInfo } = incidentDetailData;

  // 事案情報のEnum変換
  const translatedIncidentInfo = {
    ...incidentInfo,
    incidentType: getIncidentTypeLabel(incidentInfo.incidentType),
    incidentSection: getIncidentSectionLabel(incidentInfo.incidentSection),
    telegramTargetAddress: incidentInfo.telegramTargetAddress ?? '',
    targetAddressOther: incidentInfo.targetAddressOther ?? '',
    occurrenceAddress: incidentInfo.occurrenceAddress ?? '',
    telegramTargetAddressType: getIncidentTelegramTargetAddressTypeLabel(
      incidentInfo.telegramTargetAddressType
    ),
    activityType: getIncidentActivityTypeLabel(incidentInfo.activityType),
    dispatchStatus: getIncidentDispatchStatusLabel(incidentInfo.dispatchStatus),
    loadData: incidentInfo.roadData
      ? {
          ...incidentInfo.roadData,
          loadType: getIncidentRoadTypeLabel(incidentInfo.roadData.roadType),
          loadDirection: getIncidentRoadDirectionLabel(
            incidentInfo.roadData.roadDirection
          ),
        }
      : undefined,
    doctorsOnDispatch: incidentInfo.doctorsOnDispatch ?? [],
  };

  // 救急隊情報のEnum変換
  const translatedEmsTeamInfo = {
    ...emsTeamInfo,
    emsTeamMembers: emsTeamInfo.emsTeamMembers.map(member => ({
      ...member,
      position: getEmsTeamMemberPositionLabel(member.position),
    })),
  };

  const boolToLabel = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value ? 'あり' : 'なし';
  };

  // 傷病者情報のEnum変換
  const translatedPatients = patientInfo.map(patient => ({
    ...patient,
    baseInfo: {
      ...patient.baseInfo,
      hasCall: boolToLabel(patient.baseInfo?.hasCall),
      sex: getPatientSexLabel(patient.baseInfo?.sex),
      occupation: getOccupationLabel(patient.baseInfo?.occupation),
      ambulanceWithTraining: getAmbulanceWithTrainingLabel(
        patient.baseInfo?.ambulanceWithTraining
      ),
      // 関係者連絡用フィールド
      familyContact: patient.baseInfo?.familyContact,
      contactFamily: patient.baseInfo?.contactFamily,
      contactPerson: patient.baseInfo?.contactPerson,
      policeContact: patient.baseInfo?.policeContact,
      policeContactComment: patient.baseInfo?.policeContactComment,
      ambulanceWithTrainingComment:
        patient.baseInfo?.ambulanceWithTrainingComment,
      // 同乗者リスト
      accompanyingList: patient.baseInfo?.accompanyingList?.map(item => ({
        ...item,
        accompanying: getAccompanyingLabel(item.accompanying),
      })),
      // 福祉データ
      welfareData: patient.baseInfo?.welfareData
        ? {
            welfareDistrict: getWelfareDistrictLabel(
              patient.baseInfo.welfareData.welfareDistrict
            ),
            welfareDistrictOther:
              patient.baseInfo.welfareData.welfareDistrictOther ?? '',
          }
        : undefined,
      collaboration:
        patient.baseInfo?.collaboration?.map(item =>
          getCollaborationLabel(
            item as Parameters<typeof getCollaborationLabel>[0]
          )
        ) ?? [],
      collaborationOthers1:
        patient.baseInfo?.collaborationOthers1?.map(item =>
          getCollaborationOther1Label(item)
        )?.[0] ?? '',
      collaborationOthers2:
        patient.baseInfo?.collaborationOthers2?.map(item =>
          getCollaborationOther2Label(item)
        )?.[0] ?? '',
      collaborationOther: patient.baseInfo?.collaborationOther ?? '',
      collaborationTiming: getCollaborationTimingLabel(
        patient.baseInfo?.collaborationTiming as Parameters<
          typeof getCollaborationTimingLabel
        >[0]
      ),
      collaborationData: patient.baseInfo?.collaborationData
        ? {
            ...patient.baseInfo.collaborationData,
            requestedAt: patient.baseInfo.collaborationData.requestedAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.requestedAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            contactAt: patient.baseInfo.collaborationData.contactAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.contactAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            accommodationAt: patient.baseInfo.collaborationData.accommodationAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.accommodationAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            firefightingTeamArrivalAt: patient.baseInfo.collaborationData
              .firefightingTeamArrivalAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.firefightingTeamArrivalAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            firefightingTeamContactAt: patient.baseInfo.collaborationData
              .firefightingTeamContactAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.firefightingTeamContactAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            firefightingTeamCprStartedAt: patient.baseInfo.collaborationData
              .firefightingTeamCprStartedAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData
                    .firefightingTeamCprStartedAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            emsTeamCprStartedAt: patient.baseInfo.collaborationData
              .emsTeamCprStartedAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.emsTeamCprStartedAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            alsArrivalAt: patient.baseInfo.collaborationData.alsArrivalAt
              ? formatDateTime(
                  patient.baseInfo.collaborationData.alsArrivalAt,
                  'yyyy/MM/dd HH:mm'
                )
              : '',
            drHeliRequestedTiming: getDrHeliRequestedTimingLabel(
              patient.baseInfo.collaborationData
                .drHeliRequestedTiming as Parameters<
                typeof getDrHeliRequestedTimingLabel
              >[0]
            ),
            isFirefightingTeamDispatched: boolToLabel(
              patient.baseInfo.collaborationData.isFirefightingTeamDispatched
            ),
            isAls: boolToLabel(patient.baseInfo.collaborationData.isAls),
            hasCollaborationCommunicationSheet: boolToLabel(
              patient.baseInfo.collaborationData
                .hasCollaborationCommunicationSheet
            ),
            isAccompanying: boolToLabel(
              patient.baseInfo.collaborationData.isAccompanying
            ),
          }
        : undefined,
    },
    cpaMode: {
      isCpa: patient.isCpa ?? false,
      hasCpa: patient.cpaData?.hasCpa,
      lastHealthedAt: patient.cpaData?.lastHealthedAt
        ? formatDateTime(patient.cpaData.lastHealthedAt, 'yyyy/MM/dd HH:mm')
        : null,
      initialEcg: getCpaInitialEcgLabel(patient.cpaData?.initialEcg),
      isMedicalHistory: patient.requestSummary?.isMedicalHistory,
      medicalHistory:
        patient.requestSummary?.medicalHistory?.map(history =>
          getMedicalHistoryLabel(history)
        ) ?? [],
      medicalHistoryComment: patient.requestSummary?.medicalHistoryComment,
    },
    requestSummary: patient.requestSummary
      ? {
          ...patient.requestSummary,
          // 発症概要
          onsetSummary: patient.requestSummary.onsetSummary ?? '',
          // 現着時状況概要
          siteArrivalSituationSummary:
            patient.requestSummary.siteArrivalSituationSummary ?? '',
          // 既往歴
          isMedicalHistory: patient.requestSummary.isMedicalHistory,
          medicalHistory:
            patient.requestSummary.medicalHistory?.map(history =>
              getMedicalHistoryLabel(history)
            ) ?? [],
          medicalHistoryComment:
            patient.requestSummary.medicalHistoryComment ?? '',
          // 服薬状況
          hasMedicationStatus: patient.requestSummary.hasMedicationStatus,
          medicationStatus: patient.requestSummary.medicationStatus ?? [],
          medicationStatusComment:
            patient.requestSummary.medicationStatusComment ?? '',
          // アレルギー
          allergies: patient.requestSummary.allergies ?? '',
          // 最終食事
          lastMealComment: patient.requestSummary.lastMealComment ?? '',
          // ADL
          adl: getADLLabel(patient.requestSummary.adl),
          adlExcretion: getADLExcretionLabel(
            patient.requestSummary.adlExcretion
          ),
          adlCommunication: getADLCommunicationLabel(
            patient.requestSummary.adlCommunication
          ),
          // 飲酒
          drunkText: patient.requestSummary.drunkText ?? '',
          // 搬送体位
          transportBodyPosition: getTransportBodyPositionLabel(
            patient.requestSummary.transportBodyPosition
          ),
          // かかりつけ医
          primaryCareHospitals:
            patient.requestSummary.primaryCareHospitals ?? [],
          primaryCareHospitalId:
            patient.requestSummary.primaryCareHospitalId ?? '',
        }
      : undefined,
    // バイタルのEnum変換
    vitals:
      patient.vitals?.map(vital => ({
        ...vital,
        data: vital.data
          ? {
              ...vital.data,
              measureTiming: vital.data.measureTiming
                ? getMeasureTimingLabel(
                    vital.data.measureTiming as Parameters<
                      typeof getMeasureTimingLabel
                    >[0]
                  )
                : '',
              ecgWaveform: getECGWaveformLabel(
                vital.data.ecgWaveform as Parameters<
                  typeof getECGWaveformLabel
                >[0]
              ),
              ecgRemarks: getECGRemarksLabel(vital.data.ecgRemarks),
              pupilReactionLeft: getPupilReactionLabel(
                vital.data.pupilReactionLeft as Parameters<
                  typeof getPupilReactionLabel
                >[0]
              ),
              pupilReactionRight: getPupilReactionLabel(
                vital.data.pupilReactionRight as Parameters<
                  typeof getPupilReactionLabel
                >[0]
              ),
              o2Means: getO2MeansLabel(
                vital.data.o2Means as Parameters<typeof getO2MeansLabel>[0]
              ),
            }
          : undefined,
      })) ?? [],
    // 接触時の所見のEnum変換
    findingsEmsContact: patient.findingsEmsContact
      ? {
          ...patient.findingsEmsContact,
          // 顔貌（単一値として処理、配列の場合は最初の要素を取得）
          facialAppearance: getFacialAppearanceLabel(
            Array.isArray(patient.findingsEmsContact.facialAppearance)
              ? patient.findingsEmsContact.facialAppearance[0]
              : patient.findingsEmsContact.facialAppearance
          ),
          // 表情（単一値として処理、配列の場合は最初の要素を取得）
          facialExpression: getFacialExpressionLabel(
            Array.isArray(patient.findingsEmsContact.facialExpression)
              ? patient.findingsEmsContact.facialExpression[0]
              : patient.findingsEmsContact.facialExpression
          ),
          position: getPositionLabel(patient.findingsEmsContact.position),
          // 死亡の兆候
          deathSign: patient.findingsEmsContact.deathSign,
          deathSignRigorMortis: patient.findingsEmsContact.deathSignRigorMortis,
          deathSignLivorMortis: patient.findingsEmsContact.deathSignLivorMortis,
          deathSignOther: patient.findingsEmsContact.deathSignOther,
          // reveals (所見)
          reveals: patient.findingsEmsContact.reveals,
        }
      : undefined,
    // バイスタンダー
    bystander: patient.bystander
      ? {
          ...patient.bystander,
          cardiacArrestWitnessed: boolToLabel(
            patient.bystander.cardiacArrestWitnessed
          ),
          cpr: boolToLabel(patient.bystander.cpr),
          aed: boolToLabel(patient.bystander.aed),
          aedInitiallyUsedAt: patient.bystander.aedInitiallyUsedAt
            ? formatDateTime(
                patient.bystander.aedInitiallyUsedAt,
                'yyyy/MM/dd HH:mm'
              )
            : '',
          isVentilationPerformed: boolToLabel(
            patient.bystander.isVentilationPerformed
          ),
          ventilationData: patient.bystander.ventilationData
            ? {
                ...patient.bystander.ventilationData,
                startedAt: patient.bystander.ventilationData.startedAt
                  ? formatDateTime(
                      patient.bystander.ventilationData.startedAt,
                      'yyyy/MM/dd HH:mm'
                    )
                  : '',
              }
            : undefined,
          isChestCompressionPerformed: boolToLabel(
            patient.bystander.isChestCompressionPerformed
          ),
          chestCompressionData: patient.bystander.chestCompressionData
            ? {
                ...patient.bystander.chestCompressionData,
                startedAt: patient.bystander.chestCompressionData.startedAt
                  ? formatDateTime(
                      patient.bystander.chestCompressionData.startedAt,
                      'yyyy/MM/dd HH:mm'
                    )
                  : '',
              }
            : undefined,
          bystanderType: getBystanderTypeLabel(
            patient.bystander.bystanderType as Parameters<
              typeof getBystanderTypeLabel
            >[0]
          ),
          bystanderTypeOther: patient.bystander.bystanderTypeOther ?? '',
          qualificationOther: patient.bystander.qualificationOther ?? '',
          isAedPadAttached: boolToLabel(patient.bystander.isAedPadAttached),
          aedApplyType: patient.bystander.aedApplyType ?? '',
          aedAnalysisUnknownReason:
            patient.bystander.aedAnalysisUnknownReason ?? '',
          aedCountType: getAedCountLabel(patient.bystander.aedCountType),
          treatments:
            patient.bystander.treatments?.map(treatment => ({
              ...treatment,
              treatment: getBystanderTreatmentLabel(
                treatment.treatment as Parameters<
                  typeof getBystanderTreatmentLabel
                >[0]
              ),
              treatmentPerson: getBystanderTreatmentPersonLabel(
                treatment.treatmentPerson as Parameters<
                  typeof getBystanderTreatmentPersonLabel
                >[0]
              ),
            })) ?? [],
        }
      : undefined,
    // 応急処置のEnum変換
    treatments:
      patient.treatments?.map(treatment => ({
        ...treatment,
        data: treatment.data
          ? {
              ...treatment.data,
              generalTreatments:
                treatment.data.generalTreatments?.map(t =>
                  getGeneralTreatmentsLabel(t)
                ) ?? [],
            }
          : undefined,
      })) ?? [],
  }));

  return {
    incidentInfo: translatedIncidentInfo,
    emsTeamInfo: translatedEmsTeamInfo,
    patients: translatedPatients,
  };
};
